import { Controller } from 'stimulus';
import Modal from 'bootstrap/js/src/modal';

/**
 * WeakMap linking elements to their associated Modal object
 * to keep track tidily while allowing garbage collection when
 * the elements disappear
 * @private
 */
const INSTANCES = new WeakMap();

const SELECTOR_MODAL = '.modal';
const ATTRIBUTE_DATA_DIALOG_PREVENT_DEFAULT =
  'data-aria-dialog-prevent-default';

/**
 * A single controller to manage modals on the page
 */
export default class extends Controller {
  toggle(event) {
    // Allow ctrl-clicks and other specials ways of clicking the link
    // to function as before
    if (hasModifiers(event)) return;

    const id = event.currentTarget.getAttribute('aria-controls');
    const target = document.getElementById(id);

    if (!target) {
      if (process.env.NODE_ENV == 'development') {
        console.error(
          `No target with this id exists to open modal`,
          `id: ${id}`,
          `target: ${event.currentTarget}`
        );
      }
      return;
    }

    const modal = this.instanceFor(target);

    // Only prevent default after ensuring there's a target dialog to open
    // so that dialogs hooked on links follow the link in case there's no dialog
    // on the page. Preventing default would block turbo requests
    if (shouldPreventDefault(event.currentTarget)) event.preventDefault();

    modal.toggle(event.target);
  }

  close(event) {
    if (shouldPreventDefault(event.currentTarget)) event.preventDefault();

    // There may be multiple modals on the page. Find the instance to close with either:
    // - a specified ID from the triggering event
    // - the modal that the event occurred inside (i.e cusing the close button)
    const modalEl =
      document.getElementById(event.detail?.modal_id) ||
      event.target.closest(SELECTOR_MODAL);

    if (modalEl) {
      const modal = this.instanceFor(modalEl);

      if (isTransitioningIn(modalEl)) {
        document.addEventListener(
          'shown.bs.modal',
          () => {
            modal.hide();
          },
          { once: true }
        );
      } else {
        modal.hide();
      }
    }
  }

  instanceFor(target) {
    return INSTANCES.get(target) || this.createModal(target);
  }

  createModal(target) {
    const modal = new Modal(target);
    INSTANCES.set(target, modal);

    return modal;
  }
}

/**
 * Calling `hide()` will do nothing if the modal hasn't finished transitioning in.
 * This checks for current transitions on the `.modal-dialog`, which is the element
 * with the longest transition (the modalEl uses half the speed to transition).
 * @param {HTMLElement} modalEl
 * @returns {boolean}
 */
function isTransitioningIn(modalEl) {
  const el = modalEl.querySelector('.modal-dialog');
  const animations = el.getAnimations();

  return animations.some(Boolean);
}

function shouldPreventDefault(el) {
  return el.getAttribute(ATTRIBUTE_DATA_DIALOG_PREVENT_DEFAULT) !== 'false';
}

/**
 * Checks whether the given event has any modifier (Ctrl, Shift, Alt, meta)
 * or is a right click
 * @param {MouseEvent} event
 */
function hasModifiers(event) {
  return (
    event.which > 1 ||
    event.metaKey ||
    event.ctrlKey ||
    event.shiftKey ||
    event.altKey
  );
}
